/**
 * registration-api rejects a card number it cannot use with an ASP.NET model-validation 400
 * ({ errors: { "UserTemporary.CreditCard": ["Please specify a valid credit card number"] } })
 * rather than with the { result: "<FunnelCodeResponse>" } body every other funnel rejection uses,
 * so there is no numeric code for ValidateFunnelMessages to switch on. Left alone, that response
 * falls through to the default branch, lands in FunnelMessage.Code as raw prose, matches nothing
 * in funnel-messages.json and is shown as a toast, while ValidateCreditCardAttempts stays false -
 * the attempt is never counted and every counter-driven behaviour (the "012" final-attempt
 * warning, the decline-v2 redirect and the exit pop-up) silently stops firing.
 *
 * ErrorInterceptor encodes that shape as this sentinel and keeps the backend's own text after the
 * separator, so the funnel layer can treat it as the card rejection it is while still showing what
 * the backend said. The channel between the interceptor and the funnel components is a plain
 * string (see FunnelService.ValidateFunnelMessages and every funnel's ValidateFunnelResponse),
 * which is why the message travels encoded in one instead of as an object.
 */
const CreditCardValidationFailedPrefix = "CreditCardValidationFailed::";

/** Matches "UserTemporary.CreditCard" and any nested card field the API may report instead. */
const CreditCardFieldPattern = /creditcard/i;

export function buildCreditCardValidationError(message: string): string {
    // The caller joins several messages with "<br>" separators and leaves a trailing one; kept out
    // of the payload so the text does not open a blank line inside the funnel message box.
    return `${CreditCardValidationFailedPrefix}${(message ?? "").replace(/(<br>)+$/, "")}`;
}

export function isCreditCardValidationError(funnelCodeResponse: string): boolean {
    return typeof funnelCodeResponse == "string" && funnelCodeResponse.startsWith(CreditCardValidationFailedPrefix);
}

export function getCreditCardValidationMessage(funnelCodeResponse: string): string {
    return isCreditCardValidationError(funnelCodeResponse)
        ? funnelCodeResponse.substring(CreditCardValidationFailedPrefix.length)
        : "";
}

/** True when a model-validation error payload blames a credit card field. */
export function payloadBlamesCreditCard(errors: any): boolean {
    return Object.keys(errors ?? {}).some(field => CreditCardFieldPattern.test(field));
}
